import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useEffect, useState } from 'react'
import type { GeoJsonObject, Feature, Geometry } from 'geojson'

// Fix para iconos de Leaflet
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

L.Marker.prototype.options.icon = DefaultIcon

interface MapComponentProps {
  geojsonData?: string | GeoJsonObject | null
  data?: any[]
  title?: string
  mapType?: 'markers' | 'heatmap' | 'choropleth'
  centerLat?: number
  centerLon?: number
  zoom?: number
  loading?: boolean
  /** Si se proporciona, el popup al hacer clic solo muestra estos campos (ej. selectedFields de BMP) */
  displayFields?: string[]
  style?: (feature: Feature<Geometry, any>) => L.PathOptions
  onEachFeature?: (feature: Feature<Geometry, any>, layer: L.Layer) => void
}

// Componente para ajustar bounds automáticamente basado en GeoJSON
function FitBoundsGeoJSON({ geojson }: { geojson: GeoJsonObject | null }) {
  const map = useMap()

  useEffect(() => {
    if (geojson) {
      const bounds = L.geoJSON(geojson).getBounds()
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
      }
    }
  }, [geojson, map])

  return null
}



// Construye onEachFeature según displayFields (solo esos campos en el popup) o todos
function buildOnEachFeature(displayFields?: string[]) {
  return (feature: Feature<Geometry, any>, layer: L.Layer) => {
    if (!feature.properties) return
    const props = feature.properties as Record<string, unknown>
    const keys = displayFields?.length
      ? displayFields.filter((k) => Object.prototype.hasOwnProperty.call(props, k))
      : Object.keys(props)
    const popupContent = keys
      .map((key) => `<strong>${key}:</strong> ${props[key] != null ? String(props[key]) : '—'}`)
      .join('<br/>')
    if (popupContent) layer.bindPopup(popupContent)
  }
}

export function MapComponent({
  geojsonData,
  title,
  mapType = 'markers',
  centerLat = 41.5,
  centerLon = -4.5,
  zoom = 8,
  loading = false,
  displayFields,
  onEachFeature: customOnEachFeature
}: MapComponentProps) {
  const [geojson, setGeojson] = useState<GeoJsonObject | null>(null)
  const [error, setError] = useState<string | null>(null)
  const onEachFeature = customOnEachFeature ?? buildOnEachFeature(displayFields)

  // Cargar GeoJSON desde URL si se proporciona un string
  useEffect(() => {
    if (!geojsonData) {
      setGeojson(null)
      return
    }
    

    // Si es un string (URL), cargar el GeoJSON
    if (typeof geojsonData === 'string') {  
      fetch(geojsonData)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
          return res.json()
        })
        .then(data => {
          setGeojson(data)
          setError(null)
        })
        .catch(err => {
          console.error('Error loading GeoJSON:', err)
          setError(`Error al cargar el GeoJSON: ${err.message}`)
          setGeojson(null)
        })
    } else {
      // Si ya es un objeto GeoJSON, usarlo directamente
      setGeojson(geojsonData)
      setError(null)
    }
  }, [geojsonData])



  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-red-500 border border-red-200 rounded">
          {error}
        </div>
      </div>
    )
  }

  if (!geojson) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500 border border-gray-200 rounded">
          No hay datos GeoJSON disponibles
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <div className="h-[400px] rounded overflow-hidden border border-gray-200">
        <MapContainer
          center={[centerLat, centerLon]}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
          />

          <FitBoundsGeoJSON geojson={geojson} />

          <GeoJSON
            key={JSON.stringify(geojson)} // Forzar re-render cuando cambie
            data={geojson}
            //style={getStyle()}
            onEachFeature={onEachFeature}
          />
        </MapContainer>
      </div>

      {/* Leyenda (si es heatmap o choropleth) */}
      {(mapType === 'heatmap' || mapType === 'choropleth') && (
        <div className="mt-4 text-xs text-gray-600">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span>Bajo</span>
            <div className="flex">
              <div className="w-8 h-3 bg-blue-500"></div>
              <div className="w-8 h-3 bg-blue-400"></div>
              <div className="w-8 h-3 bg-blue-300"></div>
              <div className="w-8 h-3 bg-blue-200"></div>
              <div className="w-8 h-3 bg-red-200"></div>
              <div className="w-8 h-3 bg-red-300"></div>
              <div className="w-8 h-3 bg-red-400"></div>
              <div className="w-8 h-3 bg-red-500"></div>
            </div>
            <span>Alto</span>
          </div>
          <p className="text-center">
            {mapType === 'heatmap' ? 'Heatmap: intensidad del valor' : 'Choropleth: distribución por área'}
          </p>
        </div>
      )}
    </div>
  )
}